import * as stylex from '@stylexjs/stylex'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'

import { colors, fonts, fontSizes } from '../tokens/tokens.stylex'

const styles = stylex.create({
  text: { margin: 0, fontFamily: fonts.sans, lineHeight: 1.5 },
  body: { fontSize: fontSizes.body },
  small: { fontSize: fontSizes.small },
  label: { fontSize: fontSizes.label, fontFamily: fonts.interface },
  default: { color: colors.foreground },
  muted: { color: colors.muted },
  danger: { color: colors.danger },
  heading: {
    margin: 0,
    fontFamily: fonts.interface,
    fontWeight: 500,
    lineHeight: 1.25,
    letterSpacing: '-0.02em',
    color: colors.foreground,
  },
  title: { fontSize: fontSizes.title },
  display: { fontSize: fontSizes.display },
  workspace: { fontSize: fontSizes.title, fontFamily: fonts.sans, fontWeight: 600 },
  section: { fontSize: fontSizes.body, fontFamily: fonts.sans, fontWeight: 600 },
})

type TextOptions = {
  as?: 'span' | 'p' | 'div'
  size?: 'body' | 'small' | 'label'
  tone?: 'default' | 'muted' | 'danger'
}

export type TextProps = Omit<ComponentPropsWithoutRef<'span'>, 'className' | 'style' | 'color'> & TextOptions

/**
 * Use Text for product body copy, supporting text, and theme-styled labels.
 * Choose Heading for document sections and EditorialText for public reading pages.
 * Applications supply content, choose its semantic element, and load fonts.
 *
 * Status: supported.
 */
export function Text({ as: Tag = 'span', size = 'body', tone = 'default', ...props }: TextProps) {
  return <Tag {...props} {...stylex.props(styles.text, styles[size], styles[tone])} />
}

export type HeadingProps = {
  children: ReactNode
  level: 1 | 2 | 3 | 4 | 5 | 6
  variant?: 'title' | 'display' | 'workspace' | 'section'
  id?: string
}

/**
 * Use Heading for a product document section with visual size independent of its semantic level.
 * Choose Text for supporting copy and EditorialHeading for public reading pages.
 * Applications supply the content and select the level that fits the document hierarchy.
 *
 * Status: supported.
 */
export function Heading({ level, variant = 'title', children, id }: HeadingProps) {
  const Tag = `h${level}` as const

  return (
    <Tag id={id} {...stylex.props(styles.heading, styles[variant])}>
      {children}
    </Tag>
  )
}
